import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import IPurchase_Order from "@models/IPurchase_Order";
import { useContext } from "react";
import { OwCheckbox } from "@components/shared/OwCheckbox/OwCheckbox";
import { OwDeleteEditActions } from "@components/shared/OwDeleteEditActions/OwDeleteEditActions";
import { OwPill } from "@components/shared/OwPill/OwPill";
import { OwLabel } from "@components/shared/OwLabel/OwLabel";
import { Link } from "react-router-dom";
import UserService from "@services/UserService";
import AddressDisplay from "@components/shared/AddressDisplay/AddressDisplay";
import OwGoogleStaticMap from "@components/shared/OwGoogleStaticMap/OwGoogleStaticMap";
import IOwTableCols from "@models/IOwTableCols";
import SortOrderEnum from "@models/SortOrderEnum";
import _ from "lodash";
import IAction from "@models/IAction";
import PoOrderBy from "@models/PoOrderByEnum";
import IntegrationsConnectedBar from "@components/shared/IntegrationsConnectedBar/IntegrationsConnectedBar";

export interface PurchaseOrderColsProps {
    toggleConfirmDelete: (row: IPurchase_Order, index: number) => void;
    purchaseOrders: IPurchase_Order[];
    orderBy: PoOrderBy;
    orderDir: SortOrderEnum;
    setOrderBy: (orderBy: PoOrderBy) => void;
    setOrderDir: (orderDir: SortOrderEnum) => void;
    selectedIds: Set<number>;
    onSelectRow: (row: IPurchase_Order) => void;
    createQbdPurchaseOrders: () => void;
    reconcilePurchaseOrders: () => void;
}

export const usePurchaseOrdersCols = ({
    toggleConfirmDelete,
    purchaseOrders,
    orderBy,
    orderDir,
    setOrderBy,
    setOrderDir,
    selectedIds,
    onSelectRow,
    createQbdPurchaseOrders,
    reconcilePurchaseOrders,
}: PurchaseOrderColsProps) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const hasOneCheckedFn = (): boolean => {
        return selectedIds.size === 0;
    };

    const getActions = () => {
        const _actions: IAction[] = [];

        if (!userModel.isVendorSourceUser) {
            // _actions.push(
            //     {
            //         name: "mark_incoming",
            //         disabledFn: hasOneCheckedFn,
            //     },
            //     {
            //         name: "start_receive",
            //         disabledFn: hasOneCheckedFn,
            //     }
            // );

            if (supplierModel.Supplier_Integration?.has_qbd && userModel.isSupplierUser) {
                _actions.push({
                    name: "quickbooks_desktop",
                    isGroup: true,
                    groupActions: [
                        {
                            name: "create_purchase_order",
                            fn: createQbdPurchaseOrders,
                            disabledFn: () => {
                                return selectedIds.size === 0;
                            },
                        },
                        {
                            name: "reconcile_purchase_orders",
                            fn: reconcilePurchaseOrders,
                            disabledFn: () => {
                                return false;
                            },
                        },
                    ],
                });
            }
        }

        return _actions;
    };

    const renderActionsFn = (row: IPurchase_Order, index: number) => {
        // Check if this row is selected
        const isChecked = selectedIds.has(row.id);

        return (
            <div className={"d-flex"}>
                {userModel.isVendorSourceUser ? null : (
                    <>
                        <OwCheckbox
                            checked={isChecked}
                            className={"me-4 d-print-none pointer"}
                            label={""}
                            skipTranslate={true}
                            name={""}
                            fn={() => {
                                onSelectRow(row);

                                // Handle row highlighting
                                const elem = document.getElementById(`row-${index}`);
                                if (elem) {
                                    if (!isChecked) {
                                        // Add highlight if being checked
                                        elem.classList.add("highlight-row");
                                    } else {
                                        // Remove highlight if being unchecked
                                        elem.classList.remove("highlight-row");
                                    }
                                }
                            }}
                            truthyVal={true}
                            falsyVal={false}
                        />

                        <OwDeleteEditActions
                            isEditMode={false}
                            onTrashClickFn={() => {
                                toggleConfirmDelete(row, index);
                            }}
                            onEditClickFn={() => {}}
                            hideEdit={true}
                        />
                    </>
                )}
            </div>
        );
    };

    const renderNameFn = (row: IPurchase_Order) => {
        return (
            <div className={"d-flex flex-column justify-content-start align-items-start"}>
                <div className={"d-flex flex-row align-items-center"}>
                    <Link to={`/purchase_orders/${row.guid}`}>{row.name}</Link>
                    {supplierModel.Supplier_Integration?.has_qbd && (
                        <IntegrationsConnectedBar
                            showIntegrations={{
                                quickbooksDesktop: true,
                            }}
                            supplierModel={supplierModel}
                            className={"ms-2"}
                            qboConnected={!!row.qbd_TxnID}
                        />
                    )}
                </div>

                <div className={"mt-2"}>
                    <OwPill
                        title={row.is_emailed ? "emailed" : "not_emailed"}
                        leftIconClass={"fad text-white " + (row.is_emailed ? "fa-envelope-circle-check" : "fa-envelope")}
                        className={"px-2 " + (row.is_emailed ? "bg-success" : "bg-danger")}
                        labelClassName={"text-white"}
                    />
                </div>
            </div>
        );
    };

    const renderVendorSourceFn = (row: IPurchase_Order) => {
        return (
            <div>
                {userModel.isVendorSourceUser ? (
                    row.Vendor_Source?.name
                ) : (
                    <Link to={`/vendor_sources?name=${row.Vendor_Source?.name}`}>{row.Vendor_Source?.name}</Link>
                )}
            </div>
        );
    };

    const renderInternalNoteFn = (row: IPurchase_Order) => {
        return <div>{row.internal_note}</div>;
    };

    const renderOrderFn = (row: IPurchase_Order) => {
        if (row.Order) {
            return (
                <div>
                    <Link to={`/orders/${row.Order?.guid}/entry`}>{row.Order.name}</Link>
                </div>
            );
        } else {
            return null;
        }
    };

    const renderPurchaseOrderStateFn = (row: IPurchase_Order) => {
        return (
            <div>
                <OwLabel title={row.Purchase_Order_State?.name.toLowerCase()} noBottomMargin={true} />
            </div>
        );
    };

    const renderShipToFn = (row: IPurchase_Order) => {
        return (
            <div className={"d-flex"}>
                {supplierModel.has_google_maps ? <OwGoogleStaticMap address={row.Ship_To!} className={"me-2"} size={"120x120"} /> : null}
                <AddressDisplay address={row.Ship_To} showCompanyName={true} showName={true} />
            </div>
        );
    };

    const renderCreatedAtFn = (row: IPurchase_Order) => {
        return UserService.parseDate(row.created_at);
    };

    const renderUpdatedAtFn = (row: IPurchase_Order) => {
        return UserService.parseDate(row.updated_at);
    };

    const sortFn = (sortCol: string, sortOrder: SortOrderEnum) => {
        setOrderBy(sortCol as PoOrderBy);
        setOrderDir(sortOrder);
    };

    const sortOrderFn = () => {
        return orderDir;
    };

    const getColumns = (): IOwTableCols[] => {
        return [
            {
                title: "",
                valueProp: "",
                isActionsCol: true,
                renderFn: renderActionsFn,
                renderFnEmitsRow: true,
                // hasCheckAll: true,
                hasChevron: true,
            },
            {
                title: "name",
                valueProp: "name",
                renderFn: renderNameFn,
                renderFnEmitsRow: true,
                isSortable: true,
                sortFn,
                sortOrderFn,
            },
            {
                title: "vendor_source",
                valueProp: "Vendor_Source.name",
                renderFn: renderVendorSourceFn,
                renderFnEmitsRow: true,
                isSortable: true,
                sortFn,
                sortOrderFn,
            },
            {
                title: "internal_note",
                valueProp: "internal_note",
                renderFn: renderInternalNoteFn,
                renderFnEmitsRow: true,
                notVisible: !userModel.isSupplierUser,
            },
            {
                title: "order",
                valueProp: "order",
                renderFn: renderOrderFn,
                renderFnEmitsRow: true,
                isSortable: true,
                sortFn,
                sortOrderFn,
            },
            {
                title: "state",
                valueProp: "purchase_order_state_id",
                renderFn: renderPurchaseOrderStateFn,
                renderFnEmitsRow: true,
                isSortable: true,
                sortFn,
                sortOrderFn,
            },
            {
                title: "ship_to",
                valueProp: "",
                renderFn: renderShipToFn,
                renderFnEmitsRow: true,
            },
            {
                title: "created_at",
                valueProp: "created_at",
                renderFn: renderCreatedAtFn,
                renderFnEmitsRow: true,
                isSortable: true,
                sortFn,
                sortOrderFn,
            },
            {
                title: "updated_at",
                valueProp: "updated_at",
                renderFn: renderUpdatedAtFn,
                renderFnEmitsRow: true,
                isSortable: true,
                sortFn,
                sortOrderFn,
            },
        ];
    };

    return {
        getColumns,
        hasOneCheckedFn,
        getActions,
    };
};
